import React from 'react';
import { Card, CardContent, Typography, Grid, Chip, Alert, Box, IconButton, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Service } from '../types';
import { ServicePriceResponse } from '../helpers';
import { IProviderDTO } from '../../providers/types';

interface ServiceCardProps {
	service: Service;
	cityName: string;
	servicePrices: ServicePriceResponse;
	providerInfo?: IProviderDTO | null;
	onDelete: () => void;
	isPreview?: boolean;
}

export const ServiceCard = ({
	service,
	cityName,
	servicePrices,
	providerInfo,
	onDelete,
	isPreview = false,
}: ServiceCardProps): JSX.Element => {
	return (
		<Card
			sx={{
				marginBottom: 1.5,
				border: '1px solid #f0f0f0',
				borderRadius: 1,
				boxShadow: 'none',
				'&:hover': {
					boxShadow: 1,
					borderColor: '#e0e0e0',
				},
			}}
		>
			<CardContent sx={{ padding: '12px 16px !important' }}>
				{/* Header con nombre y botón eliminar */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1.5 }}>
					<Box sx={{ flex: 1 }}>
						<Typography variant="subtitle1" component="h3" color="primary" sx={{ fontWeight: '500', fontSize: '1rem' }}>
							{service.name}
						</Typography>
						<Box sx={{ display: 'flex', gap: 0.5, marginTop: 0.5 }}>
							<Chip
								label={service.serviceType === 'perPassenger' ? 'Por pasajero' : 'Por grupo'}
								variant="outlined"
								size="small"
								sx={{ height: '20px', fontSize: '0.7rem' }}
							/>
							<Chip
								label={cityName}
								variant="outlined"
								size="small"
								color="secondary"
								sx={{ height: '20px', fontSize: '0.7rem' }}
							/>
						</Box>
					</Box>
					{!isPreview && (
						<IconButton color="error" onClick={onDelete} size="small" sx={{ marginLeft: 1, padding: '4px' }}>
							<DeleteIcon fontSize="small" />
						</IconButton>
					)}
				</Box>

				{/* Descripción si existe */}
				{service.description && (
					<Box sx={{ marginBottom: 1 }}>
						<Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
							{service.description}
						</Typography>
					</Box>
				)}

				{/* Información del proveedor y notas - solo en modo edición */}
				{!isPreview && (service.provider || service.internalNotes) && (
					<Box sx={{ marginBottom: 1.5 }}>
						{service.provider && (
							<Box sx={{ marginBottom: 0.5 }}>
								<Typography
									variant="caption"
									color="textSecondary"
									display="block"
									sx={{ fontWeight: '500', fontSize: '0.7rem' }}
								>
									Proveedor:
								</Typography>
								{providerInfo ? (
									<Box sx={{ marginTop: 0.25 }}>
										<Chip
											label={providerInfo.fantasyName}
											variant="outlined"
											size="small"
											color="primary"
											sx={{ marginRight: 0.5, height: '18px', fontSize: '0.65rem' }}
										/>
										<Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
											{providerInfo.businessName}
										</Typography>
									</Box>
								) : (
									<Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
										Cargando información del proveedor...
									</Typography>
								)}
							</Box>
						)}

						{service.internalNotes && (
							<Alert
								severity="warning"
								variant="outlined"
								sx={{
									fontSize: '0.65rem',
									padding: '4px 8px',
									marginTop: 0.5,
									'& .MuiAlert-icon': {
										fontSize: '0.8rem',
									},
								}}
							>
								<Typography variant="caption" sx={{ fontWeight: '500', fontSize: '0.65rem' }}>
									Nota importante:
								</Typography>
								<br />
								<Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
									{service.internalNotes}
								</Typography>
							</Alert>
						)}
					</Box>
				)}

				<Divider sx={{ marginY: 1.5, borderColor: '#f5f5f5' }} />

				{/* Información de precios */}
				<Grid container spacing={1}>
					<Grid item xs={12} sm={3}>
						<Box sx={{ textAlign: 'center' }}>
							<Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem' }}>
								Tarifa Base
							</Typography>
							<Typography variant="subtitle2" color="primary" sx={{ fontWeight: '500', fontSize: '0.9rem' }}>
								${service.fareCost}
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} sm={3}>
						<Box sx={{ textAlign: 'center' }}>
							<Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem' }}>
								Precio ADT
							</Typography>
							<Typography variant="subtitle2" sx={{ fontWeight: '500', fontSize: '0.85rem' }}>
								${servicePrices.adtPrice}
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} sm={3}>
						<Box sx={{ textAlign: 'center' }}>
							<Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem' }}>
								Precio CHD
							</Typography>
							<Typography variant="subtitle2" sx={{ fontWeight: '500', fontSize: '0.85rem' }}>
								${servicePrices.chdPrice}
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} sm={3}>
						<Box sx={{ textAlign: 'center' }}>
							<Typography variant="caption" color="textSecondary" display="block" sx={{ fontSize: '0.65rem' }}>
								Precio INF
							</Typography>
							<Typography variant="subtitle2" sx={{ fontWeight: '500', fontSize: '0.85rem' }}>
								${servicePrices.infPrice}
							</Typography>
						</Box>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};
